import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Copy, Check, Music, Sparkles, MapPin, Tent, ArrowLeft } from "lucide-react";

/**
 * /share/festival-relief — three 9:16 vertical scenes built around the
 * "festival relief" pitch the user asked for:
 *
 *   Scene 1: THE PAIN — "Looking for a Download Festival campsite?"
 *            list of the 11pm scrolling tabs.
 *   Scene 2: THE RELIEF — "We did it for you" — show real Download nearby
 *            sites with distances + audience-fit. (Pulled from the live
 *            festival API so the screenshot stays current.)
 *   Scene 3: THE OFFER — "£1.99/month · 14 days free. Cancel any time."
 *
 * Designed at Instagram Reel ratio (1080×1920) but scales fluidly so the
 * agent can screenshot for Stories/TikTok with one tap.
 */

const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

const DOWNLOAD_SLUG = "download-festival-2026";

const CAPTION_LONG = `Going to Download Festival?

Stop scrolling Google Maps at 11pm. CampCompass UK now has every UK festival mapped — with vetted campsites nearby, tagged by who they suit.

Family doing their first Latitude? We flag the family-friendly sites.
Mates doing Download? We flag the late-night-tolerant sites.
Couple doing Glasto? We surface the chill off-site stays.

Free to browse. Pro is £1.99/mo for AI packing lists, weather alerts and trip planning — 14 days free, cancel anytime.

Less than a flat white. Worth every penny.

camp-compass.uk/festivals

#DownloadFestival #UKFestivals #CampCompass #FestivalCamping`;

const CAPTION_SHORT = `Festival camping ≠ 11pm Google Maps panic.

Every UK festival → vetted nearby sites → tagged for your crowd.

camp-compass.uk/festivals · £1.99/mo · 14 days free 🏕️🎶`;

function Scene({ children, label, total, idx }) {
  return (
    <div className="relative flex-shrink-0 w-[270px] sm:w-[300px] aspect-[9/16] border-2 border-[#0e0e0e] bg-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e]" data-testid={`scene-${idx}`}>
      {children}
      {/* Scene counter */}
      <div className="absolute top-3 right-3 bg-[#0e0e0e] text-white px-1.5 py-0.5 text-[9px] mono uppercase tracking-wider">
        {idx} / {total}
      </div>
      {/* Persistent brand ribbon */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#ff4500] text-white text-center py-1 text-[10px] mono uppercase tracking-[0.18em]">
        🌐 camp-compass.uk/festivals
      </div>
      <div className="absolute top-3 left-3 bg-white/95 text-[9px] mono uppercase tracking-wider px-1.5 py-0.5">{label}</div>
    </div>
  );
}

export default function FestivalReliefReel() {
  const [sites, setSites] = useState([]);
  const [siteError, setSiteError] = useState(false);

  useEffect(() => {
    // Tighter radius (10 mi) — these are the sites you can walk-back-to / quick
    // shuttle to after the headliner. Anything further isn't the relief story.
    api.get(`/festivals/${DOWNLOAD_SLUG}/nearby-sites?radius_miles=10&limit=4`)
      .then(({ data }) => {
        setSites((data?.sites || []).slice(0, 4));
      }).catch((e) => {
        // Surface the error rather than silently swallowing — masked a 422 last time.
        console.warn("Festival relief reel: nearby-sites fetch failed", e);
        setSiteError(true);
      });
  }, []);

  // Highlight the very closest site as the "hero" pick on the relief scene.
  const heroSite = sites[0];
  const restSites = sites.slice(1, 4);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="festival-relief-reel">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/launch-pack" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:text-[#0e0e0e] mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to print pack
        </Link>
        <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
          <Music className="w-3 h-3" /> Festival Relief · Download example
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">3-scene Reel · 9:16</h1>
        <p className="text-[#555] mb-6 max-w-2xl">
          Pitch built around Download Festival. The middle scene reads live data from the festival API so the screenshot stays current as you add more sites or fix descriptions.
        </p>

        {/* Three scenes side-by-side */}
        <div className="flex gap-4 overflow-x-auto pb-4 mb-8">
          {/* SCENE 1 — THE PAIN */}
          <Scene label="01 · The pain" idx={1} total={3}>
            <div className="p-5 pt-12 h-full flex flex-col">
              <div className="text-[#dc2626] text-[10px] mono uppercase tracking-[0.18em] mb-3">
                It's 11pm. Festival's in 9 days.
              </div>
              <h2 className="font-display text-[26px] leading-[1.05] tracking-tight mb-4 text-[#0e0e0e]">
                You're trying to find a <span className="text-[#dc2626]">Download</span> campsite.
              </h2>
              <div className="space-y-1.5 text-[12px] text-[#0e0e0e]/85 mono">
                <div className="border hairline px-2 py-1.5 bg-[#fafafa]">📑 Google: "campsites near Donington"</div>
                <div className="border hairline px-2 py-1.5 bg-[#fafafa]">📑 Pitchup, Cool Camping, UKCC…</div>
                <div className="border hairline px-2 py-1.5 bg-[#fafafa]">📑 Will they let me back at 3am?</div>
                <div className="border hairline px-2 py-1.5 bg-[#fafafa]">📑 Is "family-friendly" code for…</div>
                <div className="border hairline px-2 py-1.5 bg-[#fafafa]">📑 Reading every review in 4 tabs</div>
              </div>
              <div className="mt-auto pb-7 text-[14px] font-display text-[#0e0e0e]">
                There's a better way →
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — THE RELIEF: closest sites get hero treatment so the
              "look how close it is" point lands in <2 seconds. */}
          <Scene label="02 · The relief" idx={2} total={3}>
            <div className="p-5 pt-12 h-full flex flex-col bg-gradient-to-b from-[#0e0e0e] to-[#1a1a1a] text-white">
              <div className="text-[#ff4500] text-[10px] mono uppercase tracking-[0.18em] mb-2">
                One tap. Done.
              </div>
              <h2 className="font-display text-[22px] leading-[1.05] tracking-tight mb-3">
                Closest pitches to <span className="text-[#ff4500]">Download</span>
              </h2>

              {/* Hero card — the CLOSEST site, blown up. This is the bullseye. */}
              {(() => {
                const hero = heroSite || { name: "Park Farmhouse", distance_miles: 0.4, fit: "good", fit_reason: "Standard site — late returns fine." };
                return (
                  <div className="border-2 border-[#ff4500] bg-[#ff4500]/10 p-2.5 mb-2" data-testid="reel-closest-hero">
                    <div className="text-[8px] mono uppercase tracking-[0.18em] text-[#ff4500] mb-1">
                      ★ Closest pitch
                    </div>
                    <div className="font-display text-[15px] leading-tight mb-1 truncate">
                      {hero.name}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] mono">
                      <span className="bg-[#ff4500] text-white px-1.5 py-0.5">
                        {hero.distance_miles} mi away
                      </span>
                      <span className="opacity-70 truncate">{hero.fit_reason}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Runners-up — top 3 nearby */}
              <div className="text-[8px] mono uppercase tracking-[0.18em] text-white/60 mb-1.5">
                Next closest
              </div>
              <div className="space-y-1">
                {(restSites.length > 0 ? restSites : [
                  { name: "Donington Cottage", distance_miles: 1.6, fit: "caution" },
                  { name: "Weston Wood Lodges", distance_miles: 1.8, fit: "good" },
                  { name: "Hayloft Caravan Park", distance_miles: 8.2, fit: "great" },
                ]).map((s, i) => (
                  <div key={s.campsite_id || i} className="border border-white/15 bg-white/5 px-2 py-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Tent className="w-3 h-3 text-[#ff4500] flex-shrink-0" />
                      <span className="text-[11px] truncate">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[10px] mono opacity-80">{s.distance_miles} mi</span>
                      <span className={`text-[8px] mono uppercase px-1 py-0.5 ${
                        s.fit === "great" ? "bg-[#16a34a]" : s.fit === "caution" ? "bg-[#ca8a04]" : "bg-white/15"
                      }`}>
                        {s.fit === "caution" ? "check" : s.fit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {siteError && (
                <div className="text-[9px] mono text-[#ca8a04] mt-2">
                  (showing fallback — live API unreachable)
                </div>
              )}
              <div className="mt-3 text-[11px] text-white/70 leading-snug">
                ✓ Tagged for late-night returns<br />
                ✓ Distance shown, not buried<br />
                ✓ Real campers' reviews
              </div>
              <div className="mt-auto pb-7 text-[14px] font-display text-[#ff4500]">
                That's it. You've found it.
              </div>
            </div>
          </Scene>

          {/* SCENE 3 — THE OFFER */}
          <Scene label="03 · The offer" idx={3} total={3}>
            <div className="p-5 pt-12 h-full flex flex-col bg-[#ff4500] text-white">
              <div className="text-white/85 text-[10px] mono uppercase tracking-[0.18em] mb-3">
                14 days free · cancel anytime
              </div>
              <div className="font-display text-[72px] leading-none mono mb-1">£1.99</div>
              <div className="font-display text-[18px] leading-tight mb-5 opacity-90">
                per month, after the trial
              </div>
              <div className="space-y-2 text-[12px] mb-5">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Every UK festival · mapped
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> Audience-tagged campsites
                </div>
                <div className="flex items-center gap-1.5">
                  <Music className="w-3.5 h-3.5" /> AI packing lists per trip
                </div>
              </div>
              <div className="bg-white/15 backdrop-blur-sm border border-white/25 p-2.5 text-center mb-5">
                <div className="text-[11px] mono uppercase tracking-wider opacity-90">Less than a flat white</div>
                <div className="font-display text-[14px] mt-0.5">Worth every penny.</div>
              </div>
              <div className="mt-auto pb-7">
                <div className="bg-white text-[#ff4500] text-center font-display text-[16px] py-2 leading-tight">
                  camp-compass.uk
                </div>
                <div className="text-center text-[10px] mono uppercase tracking-[0.18em] mt-2 opacity-90">
                  Tap → Sign up → Sleep easy
                </div>
              </div>
            </div>
          </Scene>
        </div>

        {/* Captions for copy-paste */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <CaptionBox label="Long caption · Instagram + Facebook" text={CAPTION_LONG} />
          <CaptionBox label="Short caption · Stories + TikTok" text={CAPTION_SHORT} />
        </div>

        <Link
          to={`/festivals/${DOWNLOAD_SLUG}`}
          className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#0e0e0e] text-white hover:bg-[#ff4500] transition-colors text-xs mono uppercase tracking-wider"
        >
          <Music className="w-3.5 h-3.5" /> Preview the Download festival page
        </Link>
      </div>
    </div>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#ff4500] hover:text-[#0e0e0e]"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}
